import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, resolvePhoto, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  MessagesSquare, Send, Lock, ImagePlus, X, HandCoins, ShieldAlert, Download,
  Check, XCircle, Clock, CheckCircle2, Loader2, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const PHONE_RE = /(?:\+?\d[\s.\-]*){7,}/;
const hasPhone = (s) => {
  if (!s) return false;
  const m = s.match(PHONE_RE);
  return !!m && m[0].replace(/\D/g, "").length >= 7;
};

function Countdown({ target, onExpire }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(target) - Date.now()));
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    const tick = () => {
      const ms = Math.max(0, new Date(target) - Date.now());
      setLeft(ms);
      if (ms <= 0 && !firedRef.current) { firedRef.current = true; onExpire && onExpire(); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target, onExpire]);
  const s = Math.floor(left / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const txt = h > 0 ? `${h}h ${pad(m)}m ${pad(sec)}s` : `${pad(m)}:${pad(sec)}`;
  return <span data-testid="countdown" className="font-mono font-semibold tabular-nums">{txt}</span>;
}

export default function Messages() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(searchParams.get("c") || null);
  const [thread, setThread] = useState({ conversation: null, messages: [] });
  const [body, setBody] = useState("");
  const [imgs, setImgs] = useState([]);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerQty, setOfferQty] = useState("1");
  const [sendingOffer, setSendingOffer] = useState(false);
  const scrollRef = useRef(null);

  const autoSelectedRef = useRef(false);
  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get("/conversations");
      setConversations(r.data);
      if (!autoSelectedRef.current) {
        autoSelectedRef.current = true;
        if (typeof window !== "undefined" && window.innerWidth >= 1024 && r.data.length) {
          setActiveId((cur) => cur || r.data[0].id);
        }
      }
    } catch (e) {
      toast.error(t("messages.loadError"));
    }
  }, [t]);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/conversations/${id}/messages`);
      setThread((prev) => {
        const a = prev.messages || [], b = r.data.messages || [];
        const la = a[a.length - 1], lb = b[b.length - 1];
        const unchanged = prev.conversation?.id === r.data.conversation?.id &&
          a.length === b.length &&
          (a.length === 0 || (la.id === lb.id && la.offer_status === lb.offer_status));
        return unchanged ? prev : r.data;
      });
    } catch (e) { /* silent during polling */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (!activeId) return;
    setSearchParams({ c: activeId });
    loadThread(activeId);
  }, [activeId, loadThread, setSearchParams]);

  // Real-time: poll conversations + active thread every 5s (no page refresh)
  useEffect(() => {
    const id = setInterval(() => {
      loadConversations();
      loadThread(activeId);
    }, 5000);
    return () => clearInterval(id);
  }, [activeId, loadConversations, loadThread]);

  const lastScrollRef = useRef({ id: null, count: 0 });
  useEffect(() => {
    const count = thread.messages.length;
    const cid = thread.conversation?.id;
    const prev = lastScrollRef.current;
    if (cid !== prev.id || count > prev.count) {
      scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
    }
    lastScrollRef.current = { id: cid, count };
  }, [thread]);

  const role = thread.conversation
    ? (user?.id === thread.conversation.buyer_id ? "buyer" : "seller")
    : null;

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() && imgs.length === 0) return;
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { body, images: imgs });
      setThread((tt) => ({ ...tt, messages: [...tt.messages, data] }));
      setBody(""); setImgs([]);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.sendError"));
    }
  };

  const attach = async (files) => {
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setImgs((x) => [...x, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.attachError"));
    }
  };

  const submitOffer = async () => {
    const amount = Number(offerAmount);
    const quantity = Number(offerQty);
    if (!amount || amount <= 0 || !quantity || quantity <= 0) return;
    setSendingOffer(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/offers`, { amount, quantity });
      setThread((tt) => ({ ...tt, messages: [...tt.messages, data] }));
      setOfferOpen(false); setOfferAmount(""); setOfferQty("1");
      toast.success(t("messages.offerSent"));
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.offerError"));
    } finally { setSendingOffer(false); }
  };

  const acceptOffer = async (m) => {
    try {
      await api.post(`/conversations/${activeId}/offers/${m.id}/accept`);
      toast.success(t("messages.offerAcceptedInfo"));
      await loadThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.offerError"));
    }
  };

  const rejectOffer = async (m) => {
    try {
      await api.post(`/conversations/${activeId}/offers/${m.id}/reject`);
      toast.success(t("messages.offerRejectedToast"));
      loadThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.offerError"));
    }
  };

  const threadHasPhone = thread.messages.some((m) => hasPhone(m.body));
  const inputHasPhone = hasPhone(body);
  const showPhoneWarning = threadHasPhone || inputHasPhone;

  const pinnedOffer = [...thread.messages].reverse().find(
    (m) => m.kind === "offer" && m.offer_status === "accepted" && m.order_id && m.pay_deadline && new Date(m.pay_deadline) > new Date()
  );

  const renderOffer = (m) => {
    const mine = m.offer_by_id === user?.id;
    const statusMap = {
      pending: { label: t("messages.offerPending"), Icon: Clock, cls: "text-amber-600" },
      accepted: { label: t("messages.offerAccepted"), Icon: CheckCircle2, cls: "text-success" },
      rejected: { label: t("messages.offerRejected"), Icon: XCircle, cls: "text-destructive" },
      expired: { label: t("messages.offerExpired"), Icon: Clock, cls: "text-muted-foreground" },
    };
    const st = statusMap[m.offer_status] || statusMap.pending;
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div className="max-w-[80%] rounded-2xl border-2 border-primary/30 bg-primary/5 p-4" data-testid={`offer-${m.id}`}>
          <div className="flex items-center gap-2 text-primary font-heading font-bold">
            <HandCoins className="h-5 w-5" />
            {t("messages.offerLabel")} · {formatPrice(m.offer_amount, "FCFA")}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("messages.offerFor", { q: m.offer_quantity })}
            {mine ? ` · ${t("messages.yourOffer")}` : ""}
          </p>
          <div className={`flex items-center gap-1.5 text-sm font-semibold mt-2 ${st.cls}`} data-testid={`offer-status-${m.id}`}>
            <st.Icon className="h-4 w-4" /> {st.label}
          </div>

          {m.offer_status === "pending" && !mine && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="bg-success hover:bg-success/90 text-white" onClick={() => acceptOffer(m)} data-testid={`offer-accept-${m.id}`}>
                <Check className="h-4 w-4 mr-1.5" /> {t("messages.accept")}
              </Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => rejectOffer(m)} data-testid={`offer-reject-${m.id}`}>
                <XCircle className="h-4 w-4 mr-1.5" /> {t("messages.reject")}
              </Button>
            </div>
          )}
          {m.offer_status === "pending" && (
            <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
              <Clock className="h-3 w-3" /> {t("messages.expiresInLabel")}{" "}
              <Countdown target={m.offer_expires_at} onExpire={() => loadThread(activeId)} />
            </p>
          )}
          {m.offer_status === "accepted" && m.order_id && (
            <Button size="sm" className="mt-3 bg-primary hover:bg-primary/90" onClick={() => navigate(`/commande/${m.order_id}`)} data-testid={`offer-order-${m.id}`}>
              {t("messages.viewOrder")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Gate: wait for auth to resolve before rendering the chat (user: undefined = loading, null = not authed)
  if (user === undefined) {
    return (
      <div className="container-page py-24 flex flex-col items-center justify-center text-muted-foreground" data-testid="messages-auth-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="mt-3 text-sm">{t("common.loading")}</p>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="container-page py-24 flex flex-col items-center justify-center text-muted-foreground" data-testid="messages-auth-required">
        <Lock className="h-8 w-8" />
        <p className="mt-3 text-sm">{t("messages.selectConversation")}</p>
      </div>
    );
  }


  return (
    <div className="container-page py-10 pb-24 lg:pb-10">
      <h1 className="font-heading font-extrabold text-3xl text-secondary mb-1">{t("messages.title")}</h1>
      <p className="text-sm text-muted-foreground mb-6 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("listing.exchangeHint")}</p>

      <div className="grid lg:grid-cols-3 gap-6 lg:h-[70vh]">
        <div className={`rounded-2xl bg-card border border-border overflow-y-auto lg:h-full lg:block max-h-[calc(100dvh-16rem)] ${activeId ? "hidden" : "block"}`} data-testid="conversation-list">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">{t("messages.empty")}</div>
          ) : conversations.map((c) => {
            const other = user?.id === c.buyer_id ? c.seller_name : c.buyer_name;
            return (
              <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`conversation-${c.id}`}
                className={`w-full text-left p-4 border-b border-border transition-colors ${activeId === c.id ? "bg-muted" : "hover:bg-muted/50"}`}>
                <p className="font-semibold text-secondary text-sm truncate">{c.listing_title}</p>
                <p className="text-xs text-muted-foreground">{other}</p>
                <p className="text-xs text-muted-foreground/80 truncate mt-1">{c.last_message}</p>
              </button>
            );
          })}
        </div>

        <div className={`bg-card flex-col
          ${activeId ? "flex" : "hidden lg:flex"}
          fixed inset-x-0 top-16 bottom-16 z-30 border-t border-border
          lg:static lg:inset-auto lg:z-auto lg:top-auto lg:bottom-auto lg:col-span-2 lg:h-full lg:rounded-2xl lg:border`}>
          {thread.conversation ? (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <button onClick={() => setActiveId(null)} className="lg:hidden text-muted-foreground shrink-0" data-testid="thread-back" aria-label="Retour">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-secondary truncate">{thread.conversation.listing_title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t("messages.about")} · {user?.id === thread.conversation.buyer_id ? thread.conversation.seller_name : thread.conversation.buyer_name}
                  </p>
                </div>
              </div>

              {showPhoneWarning && (
                <div className="m-3 mb-0 rounded-xl border border-destructive/40 bg-destructive/10 p-3 flex items-start gap-2" data-testid="phone-warning">
                  <ShieldAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-xs text-destructive font-medium leading-snug">{t("messages.warnPhone")}</p>
                </div>
              )}

              {pinnedOffer && (
                <div className="m-3 mb-0 rounded-xl border-2 border-primary bg-primary/10 p-3 flex items-center justify-between gap-3" data-testid="pinned-offer">
                  <div className="min-w-0">
                    <p className="text-sm font-heading font-bold text-primary flex items-center gap-1.5">
                      <HandCoins className="h-4 w-4 shrink-0" /> {t("messages.offerAcceptedPinned")} · {formatPrice(pinnedOffer.offer_amount, "FCFA")}
                    </p>
                    <p className="text-xs text-secondary font-medium flex items-center gap-1.5 mt-1">
                      <Clock className="h-3.5 w-3.5" /> {t("messages.feePending")}
                    </p>
                  </div>
                  <Button size="sm" className="bg-primary hover:bg-primary/90 shrink-0" onClick={() => navigate(`/commande/${pinnedOffer.order_id}`)} data-testid="pinned-pay-btn">
                    {t("messages.viewOrder")}
                  </Button>
                </div>
              )}

              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
                {thread.messages.map((m) => {
                  if (m.kind === "offer") return <div key={m.id}>{renderOffer(m)}</div>;
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                        {m.images?.map((p, i) => (
                          <a key={i} href={resolvePhoto(p)} target="_blank" rel="noreferrer" download
                            className="group relative block rounded-lg mb-1.5 overflow-hidden" data-testid={`msg-image-${m.id}-${i}`}>
                            <img src={resolvePhoto(p)} alt="" className="max-h-44 w-full object-cover" />
                            <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <Download className="h-3 w-3" /> {t("messages.download")}
                            </span>
                          </a>
                        ))}
                        {m.body}
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="p-3 border-t border-border shrink-0 bg-card">
                {imgs.length > 0 && (
                  <div className="flex gap-2 mb-2">
                    {imgs.map((p) => (
                      <div key={p} className="relative h-14 w-14 rounded-lg overflow-hidden border border-border">
                        <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setImgs((x) => x.filter((i) => i !== p))} className="absolute top-0 right-0 bg-black/60 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="h-10 w-10 shrink-0 rounded-md border border-border flex items-center justify-center cursor-pointer hover:border-primary transition-colors" data-testid="attach-label">
                    <ImagePlus className="h-4 w-4 text-muted-foreground" />
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files && attach(e.target.files)} data-testid="attach-input" />
                  </label>
                  <Button type="button" variant="outline" className="h-10 shrink-0 gap-1.5 text-primary border-primary/40" onClick={() => setOfferOpen(true)} data-testid="make-offer-btn">
                    <HandCoins className="h-4 w-4" /> <span className="hidden sm:inline">{t("messages.makeOffer")}</span>
                  </Button>
                  <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("messages.placeholder")} data-testid="message-input" />
                  <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="message-send"><Send className="h-4 w-4" /></Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessagesSquare className="h-10 w-10" />
              <p className="mt-3 text-sm">{t("messages.selectConversation")}</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-primary" /> {t("messages.offerTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold text-secondary">{t("messages.offerAmount")}</label>
              <Input type="number" min={1} value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} className="mt-1.5 h-12" placeholder="FCFA" data-testid="offer-amount-input" />
            </div>
            <div>
              <label className="text-sm font-semibold text-secondary">{t("messages.offerQuantity")}</label>
              <Input type="number" min={1} value={offerQty} onChange={(e) => setOfferQty(e.target.value)} className="mt-1.5 h-12" data-testid="offer-quantity-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-primary hover:bg-primary/90" disabled={sendingOffer} onClick={submitOffer} data-testid="offer-submit">
              {t("messages.sendOffer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
